import React, { useState, useEffect } from 'react';
import {
	Alert,
	Button,
	ButtonGroup,
	Icon,
	ProgressBar,
	Searchbar,
	Table,
	TextPassage,
	Toolbar,
	ToolbarItem,
} from '../common';
import { Badge } from '@emorg-prd/standard-react';
import CheckboxFilter, { CheckboxOption } from '../common/tables/CheckboxFilter';
import Paginator from '../common/tables/Paginator';
import WorkflowStateBadge from '../common/WorkflowStateBadge';
import { authenticatedUser } from '../../authentication/authModule';
import Organization from '../../models/entities/Organization';
import SubscriptionRequest from '../../models/entities/SubscriptionRequest';
import { AlertBanner } from '../../models/interfaces/AlertBanner';
import SubscriptionRequestsService from '../../services/SubscriptionRequestsService';
import { getOrganizations } from '../common/OrganizationStorage';

export interface PolicyRequestSelectSubscriptionsProps {
	cachedSubscriptions?: SubscriptionRequest[];
	preselectedSubscription?: SubscriptionRequest;
	initialSelectedSubscriptions?: SubscriptionRequest[];
	onNext: (selectedSubscriptions: SubscriptionRequest[], cachedSubscriptions: SubscriptionRequest[]) => void;
	onCancel: () => void;
}

const PolicyExemptionRequestSelectSubscriptions = ({
	cachedSubscriptions,
	preselectedSubscription,
	initialSelectedSubscriptions,
	onNext,
	onCancel,
}: PolicyRequestSelectSubscriptionsProps) => {
	const subscriptionRequestsService = new SubscriptionRequestsService();

	const defaultStatusOptions: CheckboxOption[] = [
		{ label: 'Submitted', value: 'Submitted', checked: true },
		{ label: 'Approved', value: 'Approved', checked: true },
		{ label: 'Completed', value: 'Completed', checked: true },
		{ label: 'Awaiting Implementation', value: 'Awaiting Implementation', checked: true },
		{ label: 'Rejected', value: 'Rejected', checked: true },
		{ label: 'Deleted', value: 'Deleted', checked: false },
		{ label: 'Error', value: 'Error', checked: false },
		{ label: 'Implementation Failed', value: 'Implementation Failed', checked: false },
	];

	const defaultAzureStateOptions: CheckboxOption[] = [
		{ label: 'Enabled', value: 'Enabled', checked: true },
		{ label: 'Disabled', value: 'Disabled', checked: false },
		{ label: 'Deleted', value: 'Deleted', checked: false },
		{ label: 'Not Available', value: 'Not Available', checked: false },
	];

	const [allSubscriptions, setAllSubscriptions] = useState<SubscriptionRequest[]>(cachedSubscriptions ?? []);
	const [filteredSubscriptions, setFilteredSubscriptions] = useState<SubscriptionRequest[]>(
		cachedSubscriptions ?? []
	);
	const [subscriptionsOnPage, setSubscriptionsOnPage] = useState<SubscriptionRequest[]>([]);
	const [selectedIds, setSelectedIds] = useState<Set<number>>(() => {
		if (initialSelectedSubscriptions && initialSelectedSubscriptions.length > 0) {
			return new Set(initialSelectedSubscriptions.map((s) => s.id));
		}
		return preselectedSubscription ? new Set([preselectedSubscription.id]) : new Set();
	});
	const [isLoading, setIsLoading] = useState<boolean>(!cachedSubscriptions);
	const [searchText, setSearchText] = useState<string>('');
	const [statusOptions, setStatusOptions] = useState<CheckboxOption[]>(defaultStatusOptions);
	const [azureStateOptions, setAzureStateOptions] = useState<CheckboxOption[]>(defaultAzureStateOptions);
	const [alert, setAlert] = useState<AlertBanner>({ visible: false, variant: 'error', text: '' });

	useEffect(() => {
		if (!cachedSubscriptions) {
			loadSubscriptions();
		}
	}, []);

	useEffect(() => {
		applyFilters();
	}, [searchText, statusOptions, azureStateOptions, allSubscriptions]);

	const loadSubscriptions = async () => {
		setIsLoading(true);
		try {
			const [rawSubs, rawOrgs] = await Promise.all([
				subscriptionRequestsService.getMySubscriptions(),
				getOrganizations(),
			]);

			const organizations = rawOrgs.map((org) => new Organization(org));

			const mySubs = rawSubs
				.map((result) => {
					const sub = new SubscriptionRequest(result);
					sub.organization?.setLevels(organizations);
					if (sub.azureState == null) sub.azureState = 'Not Available';
					return sub;
				})
				.filter(
					(sub) =>
						sub.owner.id === authenticatedUser.userId ||
						sub.custodian.id === authenticatedUser.userId ||
						sub.custodianTwo?.id === authenticatedUser.userId
				);

			setAllSubscriptions(mySubs);
		} catch {
			setAlert({ visible: true, variant: 'error', text: 'Failed to load subscriptions. Please try again.' });
		} finally {
			setIsLoading(false);
		}
	};

	const matchesCheckbox = (options: CheckboxOption[], value: string) => {
		const allChecked = options.every((o) => o.checked);
		return allChecked || options.some((o) => o.checked && o.value === value);
	};

	const applyFilters = () => {
		const lower = searchText.toLowerCase().trim();
		setFilteredSubscriptions(
			allSubscriptions.filter((sub) => {
				const matchesSearch =
					!lower ||
					sub.createdSubscriptionName?.toLowerCase().includes(lower) ||
					sub.createdSubscriptionId?.toLowerCase().includes(lower) ||
					sub.owner?.displayName?.toLowerCase().includes(lower) ||
					sub.custodian?.displayName?.toLowerCase().includes(lower) ||
					sub.subscriptionUsedFor?.toLowerCase().includes(lower);

				const statusValue = sub.workflowInstance?.currentWorkflowInstanceState?.workflowState?.name ?? '';
				const matchesStatus = matchesCheckbox(statusOptions, statusValue);

				const matchesAzureState = matchesCheckbox(azureStateOptions, sub.azureState ?? 'Not Available');

				return matchesSearch && matchesStatus && matchesAzureState;
			})
		);
	};

	const handleCheckboxChange = (event: any, setter: React.Dispatch<React.SetStateAction<CheckboxOption[]>>) => {
		const { value, checked } = event.target;
		setter((prev) => {
			if (value === 'All') {
				return prev.map((o) => ({ ...o, checked }));
			}
			return prev.map((o) => (o.value === value ? { ...o, checked } : o));
		});
	};

	const toggleSelection = (id: number) => {
		setSelectedIds((prev) => {
			const next = new Set(prev);
			next.has(id) ? next.delete(id) : next.add(id);
			return next;
		});
	};

	const toggleSelectAll = () => {
		const pageIds = subscriptionsOnPage.map((s) => s.id);
		const allSelected = pageIds.every((id) => selectedIds.has(id));
		setSelectedIds((prev) => {
			const next = new Set(prev);
			if (allSelected) {
				pageIds.forEach((id) => next.delete(id));
			} else {
				pageIds.forEach((id) => next.add(id));
			}
			return next;
		});
	};

	const handleNext = () => {
		const selected = allSubscriptions.filter((sub) => selectedIds.has(sub.id));
		onNext(selected, allSubscriptions);
	};

	const clearSearch = (e: React.MouseEvent) => {
		e.preventDefault();
		setSearchText('');
	};

	const pageAllSelected = subscriptionsOnPage.length > 0 && subscriptionsOnPage.every((s) => selectedIds.has(s.id));

	return (
		<>
			<TextPassage>
				<p>
					Select the subscriptions for which you want to create a policy request. Only subscriptions where you
					are the owner or custodian are shown.
				</p>
			</TextPassage>

			{alert.visible && (
				<Alert variant={alert.variant} onClose={() => setAlert({ visible: false })} body={alert.text} />
			)}

			{isLoading && (
				<ProgressBar className="em-u-margin-top-half" indeterminate label="Loading your subscriptions..." />
			)}

			{!isLoading && (
				<>
					<Toolbar id="policy-request-toolbar">
						<ToolbarItem width="lg">
							<Searchbar
								condensed
								onClick={clearSearch}
								value={searchText}
								onChange={(e) => setSearchText(e.target.value)}
								placeholder="Search (i.e. Subscription Name, Intended Usage, Owner…)"
							/>
						</ToolbarItem>
						<ToolbarItem right>
							<ButtonGroup>
								<Button variant="secondary" size="small" onClick={onCancel}>
									<Icon icon="chevron-left" size="small" />
									<span>Cancel</span>
								</Button>
								<Button
									variant="primary"
									size="small"
									disabled={selectedIds.size === 0}
									onClick={handleNext}
								>
									<span>Next Step ({selectedIds.size} selected)</span>
									<Icon icon="chevron-right" size="small" />
								</Button>
							</ButtonGroup>
						</ToolbarItem>
					</Toolbar>

					<Toolbar>
						<ToolbarItem>
							<CheckboxFilter
								label="Status"
								options={statusOptions}
								onChange={(e) => handleCheckboxChange(e, setStatusOptions)}
							/>
						</ToolbarItem>
						<ToolbarItem>
							<CheckboxFilter
								label="Azure State"
								options={azureStateOptions}
								onChange={(e) => handleCheckboxChange(e, setAzureStateOptions)}
							/>
						</ToolbarItem>
					</Toolbar>

					<Table
						footer={
							<Paginator
								data={filteredSubscriptions}
								totalItems={allSubscriptions.length}
								onPageChange={(subs) => setSubscriptionsOnPage(subs)}
							/>
						}
					>
						<Table.Head>
							<Table.Head.Row>
								<Table.Head.Cell>
									<input
										type="checkbox"
										checked={pageAllSelected}
										onChange={toggleSelectAll}
										title="Select all on this page"
									/>
								</Table.Head.Cell>
								<Table.Head.Cell className="table__header-cell">Subscription Name</Table.Head.Cell>
								<Table.Head.Cell className="table__header-cell">Status</Table.Head.Cell>
								<Table.Head.Cell className="table__header-cell">Organization (L3/L4)</Table.Head.Cell>
								<Table.Head.Cell className="table__header-cell">Azure State</Table.Head.Cell>
							</Table.Head.Row>
						</Table.Head>
						<Table.Body>
							{subscriptionsOnPage.length === 0 ? (
								<Table.Row>
									<Table.Cell colSpan={8}>No subscriptions found.</Table.Cell>
								</Table.Row>
							) : (
								subscriptionsOnPage.map((sub) => (
									<Table.Row
										onClick={() => toggleSelection(sub.id)}
										key={sub.id}
										style={
											selectedIds.has(sub.id)
												? { background: 'var(--em-color-primary-light, #e8f0fe)' }
												: {}
										}
									>
										<Table.Cell>
											<input
												type="checkbox"
												id={`sub-check-${sub.id}`}
												checked={selectedIds.has(sub.id)}
												onChange={() => toggleSelection(sub.id)}
											/>
										</Table.Cell>
										<Table.Cell>
											<label
												htmlFor={`sub-check-${sub.id}`}
												style={{ cursor: 'pointer', fontWeight: 500 }}
											>
												{sub.createdSubscriptionName}
											</label>
										</Table.Cell>
										<Table.Cell>
											<WorkflowStateBadge
												workflowState={
													sub.workflowInstance.currentWorkflowInstanceState.workflowState
												}
												fullWidth
											/>
										</Table.Cell>
										<Table.Cell>
											{sub.organization?.L3 ? (
												<>
													<div>
														{sub.organization.L3.shortName ??
															sub.organization.L3.displayName}
													</div>
													{sub.organization.L4 && (
														<div className="em-u-font-size-small">
															{sub.organization.L4.displayName}
														</div>
													)}
												</>
											) : (
												'No organization'
											)}
										</Table.Cell>
										<Table.Cell>
											<Badge
												optionalClass={'em-u-width-100'}
												type={
													sub.azureState === 'Enabled'
														? 'em-c-badge--positive'
														: sub.azureState === 'Disabled'
															? 'em-c-badge--negative'
															: 'em-c-badge--caution'
												}
												text={sub.azureState}
											/>
										</Table.Cell>
									</Table.Row>
								))
							)}
						</Table.Body>
					</Table>
					<ButtonGroup>
						<Button variant="secondary" size="small" onClick={onCancel}>
							<Icon icon="chevron-left" size="small" />
							<span>Cancel</span>
						</Button>
						<Button variant="primary" size="small" disabled={selectedIds.size === 0} onClick={handleNext}>
							<span>Next Step ({selectedIds.size} selected)</span>
							<Icon icon="chevron-right" size="small" />
						</Button>
					</ButtonGroup>
				</>
			)}
		</>
	);
};

export default PolicyExemptionRequestSelectSubscriptions;
